import React from 'react';
import { Box, TextField, Tooltip, Typography } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import { makeStyles } from '@material-ui/core/styles';
import { Autocomplete } from '@material-ui/lab';
import { MuiPickersUtilsProvider } from '@material-ui/pickers/MuiPickersUtilsProvider';
import { KeyboardDatePicker } from '@material-ui/pickers/DatePicker';
import { DatePickerToolbar } from '@material-ui/pickers/DatePicker/DatePickerToolbar';
import DayjsUtils from '@date-io/dayjs';
import { Dayjs } from 'dayjs';
import { IFile, INumberedDocumentView, IProjectUser } from '../../api-client/autogenerated';
import PDFIcon from '../icons/PDF-icon';
import AssignedUserText from '../custom-components/AssignedUserText';
import {
  fileIsPdf,
  getTomorrowDate,
  openInNewTab,
  parseDate,
  sortLastNamesDesc,
} from '../../scripts/utils';

/**
 * The "Assign to Consultant" form. Extracted from ReviewAndReturn's CONSULTANT mode so the
 * action center and the standalone Assign to Consultant dialog render identical content.
 */
const useStyles = makeStyles(() => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'left',
  },
  fileBox: {
    display: 'flex',
    flexDirection: 'row',
    width: '90%',
    padding: '12px',
    marginBottom: '16px',
    border: '1px solid #EDECEC',
    boxSizing: 'border-box',
    borderRadius: '4px',
  },
  fileName: {
    paddingLeft: '8px',
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '11px',
    lineHeight: '29px',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    color: '#464546',
  },
  body: {
    marginBottom: '24px',
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '15px',
    lineHeight: '18px',
    color: '#464546',
  },
  formControl: {
    width: '80%',
    marginRight: '8px',
    marginBottom: '12px',
  },
  datepickerLabel: {
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '22px',
    textTransform: 'uppercase',
    color: '#616061',
  },
  datepicker: {
    background: '#F9F9F9',
    width: '80%',
    height: '35px',
  },
  textfieldLarge: {
    height: '90px',
    width: '100%',

    /* Gray / Gray 50 */
    background: '#F9F9F9',

    /* Gray / Gray 400 Brand dark */
    border: '1px solid #949494',
    borderRadius: '5px',
  },
}));

type Props = {
  document: INumberedDocumentView;
  latestFile?: IFile | null;
  consultants: IProjectUser[];
  consultant?: IProjectUser | null;
  onConsultantChange: (consultant: IProjectUser | null) => void;
  dueDate: Dayjs | null;
  onDueDateChange: (dueDate: Dayjs | null) => void;
  comment?: string;
  onCommentChange: (comment: string) => void;
};

export default function AssignConsultantForm(props: Props) {
  const classes = useStyles();
  const {
    document,
    latestFile,
    consultants,
    consultant,
    onConsultantChange,
    dueDate,
    onDueDateChange,
    comment,
    onCommentChange,
  } = props;

  return (
    <MuiPickersUtilsProvider utils={DayjsUtils}>
      <Box className={classes.content}>
        {latestFile && (
          <Box className={classes.fileBox}>
            <PDFIcon />
            <Typography className={classes.fileName}>{latestFile.name}</Typography>
            <Tooltip
              arrow
              placement="top"
              title={latestFile.isVerified ? '' : 'Upload in progress...'}
            >
              <Button
                color="primary"
                disabled={!latestFile.isVerified || !fileIsPdf(latestFile)}
                onClick={() => openInNewTab(latestFile.id)}
                style={{ height: '24px', marginLeft: 'auto', alignSelf: 'center' }}
              >
                View
              </Button>
            </Tooltip>
          </Box>
        )}
        <Typography className={classes.body}>
          The person you select as a confidential consultant will receive a notification to review
          the submittal. They will be directed to leave their response and comments in the
          “Confidential Consultant Review” section.
        </Typography>
        <FormControl
          variant="outlined"
          className={classes.formControl}
          style={{ marginBottom: 16 }}
        >
          <Autocomplete
            placeholder="Search"
            options={consultants.sort((a, b) => sortLastNamesDesc(a.user?.name, b.user?.name))}
            renderOption={(option) => (
              <AssignedUserText name={option.user!.name!} company={option.user?.company?.name} />
            )}
            getOptionLabel={(x) => {
              if (x.user) {
                if (x.user.name) return x.user.name;
              }
              return '';
            }}
            onChange={(e, item) => {
              onConsultantChange(item);
            }}
            value={consultant ?? null}
            renderInput={(params) => (
              <TextField {...params} label="Consultant" variant="outlined" />
            )}
          />
        </FormControl>
        <Typography className={classes.datepickerLabel}>due date for consultant</Typography>
        <KeyboardDatePicker
          inputVariant="outlined"
          InputProps={{ style: { padding: 0 } }}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          inputProps={{ style: { padding: '8px' } }}
          disablePast
          maxDate={document.dueDate ? parseDate(document.dueDate) : undefined}
          minDate={getTomorrowDate()}
          value={dueDate}
          ToolbarComponent={(toolbarProps) => (
            <div>
              <DatePickerToolbar {...toolbarProps} />
              {document.dueDate && (
                <Typography
                  style={{
                    position: 'absolute',
                    top: '34px',
                    right: '28px',
                    color: '#FFF',
                  }}
                >
                  Due date:
                  <br />
                  {parseDate(document.dueDate).format('MM/DD')}
                </Typography>
              )}
            </div>
          )}
          placeholder="mm/dd/yyyy"
          format="MM/DD/YYYY"
          onChange={(date) => onDueDateChange(date)}
          className={classes.datepicker}
        />
        <Typography className={classes.datepickerLabel} style={{ marginTop: 16 }}>
          comments for consultant
        </Typography>
        <TextField
          multiline
          rowsMax={4}
          placeholder="Comments..."
          value={comment}
          onChange={(event) => onCommentChange(event.target.value)}
          InputProps={{ disableUnderline: true, style: { padding: '6px' } }}
          className={classes.textfieldLarge}
        />
      </Box>
    </MuiPickersUtilsProvider>
  );
}
